/**
 * OMS Father's Day Bundle Builder.
 *
 * Commerce architecture (do not change without re-reading
 * shopify-theme/oms-fathers-day-test — this mirrors the approved local
 * campaign spec):
 *   - This script owns ONLY the paid Tee 1 + Tee 2 + Hat lines in cart. It
 *     never adds, removes, or updates the Sunnies — that stays entirely
 *     Unicorn's (window.Unicorn, an existing app already installed on this
 *     store), which detects the qualifying combination, applies its $9.90
 *     discount, and adds the free Sunnies gift line.
 *   - Every paid line this script adds carries private line-item
 *     properties (`_oms_fd_bundle: 'fathers-day-2026'`, `_oms_fd_role:
 *     'tee1'|'tee2'|'hat'`) so it can be reliably identified and
 *     reconciled on every future click — never touching a customer's own
 *     independently-added products, and never blindly re-adding on top of
 *     what's already there. See planReconciliation() / handleAddBundle().
 *   - Adding is done via Unicorn's own public API,
 *     `Unicorn.addItemsToCart({ items: [...] })` — verified empirically
 *     against the live store to use the same payload shape as Shopify's
 *     native /cart/add.js multi-item endpoint (including preserving
 *     `properties`), and to leave an existing qualifying cart (incl. the
 *     free gift line) undisturbed. Falling back to a plain Ajax Cart call
 *     only happens if window.Unicorn is genuinely unavailable (defensive,
 *     not a replacement for it). Unicorn has no equivalent "remove/update
 *     one line" API, so removals and quantity corrections for our own
 *     tagged lines go through Shopify's native /cart/change.js, keyed by
 *     each line's unique `key` (never by variant ID alone, since Tee 1
 *     and Tee 2 can legitimately share a variant).
 *   - No test-mode UI, no Cart ID, no debug output ships here — see
 *     shopify-theme/oms-fathers-day-test/... Phase 8 notes.
 */
(function () {
  'use strict';

  var initializedRoots = new WeakSet();
  var BUNDLE_TAG = 'fathers-day-2026';

  function moneyFromCents(cents) {
    var amount = (Number(cents) || 0) / 100;
    return '$' + amount.toFixed(2) + ' ' + (window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'AUD');
  }

  function bundleLineProps(role) {
    return { _oms_fd_bundle: BUNDLE_TAG, _oms_fd_role: role };
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  async function fetchCart() {
    var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not read your cart.');
    return res.json();
  }

  async function changeCartLine(key, quantity) {
    var res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) {
      var errBody = await res.json().catch(function () { return {}; });
      throw new Error(errBody.message || errBody.description || 'Could not update your bundle in cart.');
    }
    return res.json();
  }

  // Groups this bundle's own tagged cart lines by role. Untagged lines
  // (a customer's own independently-added products) are never touched —
  // only lines whose properties._oms_fd_bundle matches BUNDLE_TAG are
  // ever considered. A tagged line with an unrecognized/missing role is
  // treated as obsolete (e.g. from a prior campaign shape) and removed.
  function collectBundleLines(cart) {
    var byRole = { tee1: [], tee2: [], hat: [] };
    var obsolete = [];
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      if (props._oms_fd_bundle !== BUNDLE_TAG) return;
      var role = props._oms_fd_role;
      if (Object.prototype.hasOwnProperty.call(byRole, role)) {
        byRole[role].push(item);
      } else {
        obsolete.push(item);
      }
    });
    return { byRole: byRole, obsolete: obsolete };
  }

  // Diffs the customer's CURRENT builder selections against the CURRENT
  // (freshly-fetched) cart's bundle-owned lines and returns exactly the
  // operations needed to reconcile them — never a blind re-add of all
  // three. Legacy lines added before this tagging existed are untagged
  // and therefore invisible here by design (see file header) — they are
  // left alone, not deleted, not treated as bundle-owned.
  function planReconciliation(cart, desired) {
    var collected = collectBundleLines(cart);
    var removeKeys = [];
    var addItems = [];
    var fixQuantityKeys = [];

    ['tee1', 'tee2', 'hat'].forEach(function (role) {
      var lines = collected.byRole[role];
      var desiredId = desired[role];
      if (lines.length === 0) {
        addItems.push({ id: desiredId, quantity: 1, properties: bundleLineProps(role) });
        return;
      }
      var canonical = lines[0];
      lines.slice(1).forEach(function (extra) { removeKeys.push(extra.key); });
      if (String(canonical.variant_id) !== String(desiredId)) {
        removeKeys.push(canonical.key);
        addItems.push({ id: desiredId, quantity: 1, properties: bundleLineProps(role) });
      } else if (canonical.quantity !== 1) {
        fixQuantityKeys.push(canonical.key);
      }
      // else: unchanged, correct variant, quantity 1 — leave alone.
    });

    collected.obsolete.forEach(function (l) { removeKeys.push(l.key); });

    return { removeKeys: removeKeys, addItems: addItems, fixQuantityKeys: fixQuantityKeys };
  }

  function initBuilder(root) {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    var sectionId = root.id.replace('oms-fd-builder-', '');
    var config = (window.omsFdBundleConfig && window.omsFdBundleConfig[sectionId]) || {};
    var labels = config.labels || {};

    var slots = {
      tee1: { productId: null, variant: null, title: null },
      tee2: { productId: null, variant: null, title: null },
      hat: { productId: null, variant: null, title: null },
    };
    var cartInProgress = false;
    var hasExistingBundleLines = false;

    var summaryEl = document.getElementById('oms-fd-summary-' + sectionId);
    var stickyBarEl = document.getElementById('oms-fd-sticky-bar-' + sectionId);

    // Checked once on load (and again after every successful reconcile) so
    // the CTA can prefer "Update Bundle" over "Add Bundle to Cart" when the
    // cart already holds this bundle's lines. Best-effort only — if it
    // fails, the CTA falls back to the configured ready-state label, which
    // is still fully functional either way (see handleAddBundle).
    function refreshBundleLinePresence() {
      fetchCart()
        .then(function (cart) {
          hasExistingBundleLines = (cart.items || []).some(function (item) {
            return item.properties && item.properties._oms_fd_bundle === BUNDLE_TAG;
          });
          renderSummary();
        })
        .catch(function () {
          /* non-fatal; keep default label */
        });
    }

    function bothTeesSelected() {
      return Boolean(slots.tee1.variant && slots.tee2.variant);
    }
    function isReady() {
      return bothTeesSelected() && Boolean(slots.hat.variant) && !cartInProgress;
    }

    function railEl(key) {
      return root.querySelector('[data-oms-fd-rail="' + key + '"]');
    }

    function selectCard(key, card) {
      var productId = card.getAttribute('data-product-id');
      var variants = JSON.parse(card.getAttribute('data-variants') || '[]');
      slots[key].productId = productId;
      slots[key].variant = null;
      slots[key].title = card.querySelector('.oms-fd-tee-card__title')?.textContent || '';

      railEl(key).querySelectorAll('.oms-fd-tee-card').forEach(function (c) {
        c.classList.toggle('is-selected', c === card);
      });

      var onlyVariant = variants.length === 1 ? variants[0] : null;
      if (onlyVariant && onlyVariant.title === 'Default Title') {
        var reveal = document.getElementById('oms-fd-' + key + '-variant-reveal-' + sectionId);
        if (reveal) reveal.hidden = true;
        selectVariant(key, onlyVariant, card);
        return;
      }

      renderVariantReveal(key, variants, card);
      renderSummary();
    }

    function renderVariantReveal(key, variants, card) {
      var reveal = document.getElementById('oms-fd-' + key + '-variant-reveal-' + sectionId);
      var label = document.getElementById('oms-fd-' + key + '-variant-label-' + sectionId);
      if (!reveal || !label) return;
      var title = card.querySelector('.oms-fd-tee-card__title')?.textContent || '';
      var prompt = key === 'hat' ? 'choose an option' : 'choose your size';
      label.textContent = title + ' — ' + prompt;
      reveal.hidden = false;
      renderVariantRow(key, variants, card);
    }

    function renderVariantRow(key, variants, card) {
      var row = root.querySelector('[data-oms-fd-size-row="' + key + '"]');
      if (!row) return;
      row.innerHTML = '';
      variants.forEach(function (variant) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oms-fd-size-pill';
        btn.textContent = variant.title;
        btn.disabled = !variant.available;
        if (slots[key].variant && slots[key].variant.id === variant.id) {
          btn.classList.add('oms-fd-size-pill--selected');
        }
        if (!variant.available) {
          btn.classList.add('oms-fd-size-pill--sold-out');
          btn.title = 'Sold out';
        }
        btn.addEventListener('click', function () {
          selectVariant(key, variant, card);
        });
        row.appendChild(btn);
      });
    }

    function selectVariant(key, variant, card) {
      slots[key].variant = variant;
      var variants = JSON.parse(card.getAttribute('data-variants') || '[]');
      renderVariantRow(key, variants, card);
      renderSummary();
      advanceAfterLock(key);
    }

    function advanceAfterLock(key) {
      var behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (key === 'tee1' && slots.tee1.variant) {
        document.getElementById('oms-fd-tee2-section-' + sectionId)?.scrollIntoView({ behavior: behavior, block: 'start' });
      } else if (key === 'tee2' && slots.tee2.variant) {
        document.getElementById('oms-fd-hat-section-' + sectionId)?.scrollIntoView({ behavior: behavior, block: 'start' });
      }
    }

    ['tee1', 'tee2', 'hat'].forEach(function (key) {
      var rail = railEl(key);
      if (!rail) return;
      rail.querySelectorAll('[data-oms-fd-tee-card]').forEach(function (card) {
        if (card.disabled) return;
        card.addEventListener('click', function () {
          selectCard(key, card);
        });
      });
    });

    root.querySelectorAll('[data-oms-fd-rail-arrow]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-oms-fd-rail-target');
        var rail = railEl(target);
        var card = rail.children[0];
        if (!card) return;
        var amount = card.getBoundingClientRect().width + 12;
        rail.scrollBy({
          left: btn.getAttribute('data-oms-fd-rail-arrow') === 'prev' ? -amount : amount,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    });

    ['tee1', 'tee2', 'hat'].forEach(function (key) {
      var rail = railEl(key);
      if (!rail) return;
      var wrap = rail.closest('.oms-fd-rail-wrap');
      function updateArrows() {
        var prev = wrap.querySelector('[data-oms-fd-rail-arrow="prev"]');
        var next = wrap.querySelector('[data-oms-fd-rail-arrow="next"]');
        var maxScroll = rail.scrollWidth - rail.clientWidth;
        if (prev) prev.disabled = rail.scrollLeft <= 1;
        if (next) next.disabled = maxScroll <= 1 || rail.scrollLeft >= maxScroll - 1;
      }
      rail.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();
    });

    function nameWithOption(slot) {
      if (!slot.variant) return '';
      if (slot.variant.title === 'Default Title') return slot.title;
      return slot.title + ' · ' + slot.variant.title;
    }

    function renderSummary() {
      var rows = [
        {
          label: labels.tee1,
          value: slots.tee1.variant ? nameWithOption(slots.tee1) : (slots.tee1.productId ? labels.pickSize : labels.notSelected),
          status: slots.tee1.variant ? 'done' : 'pending',
        },
        {
          label: labels.tee2,
          value: slots.tee2.variant ? nameWithOption(slots.tee2) : (slots.tee2.productId ? labels.pickSize : labels.notSelected),
          status: slots.tee2.variant ? 'done' : 'pending',
        },
        {
          label: labels.hat,
          value: slots.hat.variant ? nameWithOption(slots.hat) : (slots.hat.productId ? labels.pickOption : labels.notSelected),
          status: slots.hat.variant ? 'done' : 'pending',
        },
        {
          label: labels.sunnies,
          value: labels.included,
          status: 'included',
        },
      ];

      var readyLabel = hasExistingBundleLines ? 'Update Bundle' : labels.ctaLabel;
      var ctaLabel = cartInProgress ? labels.ctaLoading : (isReady() ? readyLabel : labels.ctaIncomplete);
      var ready = isReady();

      var rowsHtml = rows.map(function (r) {
        return '<li class="oms-fd-summary__row oms-fd-summary__row--' + r.status + '">' +
          '<span class="oms-fd-summary__label">' + r.label + '</span>' +
          '<span class="oms-fd-summary__value">' + r.value + '</span></li>';
      }).join('');

      if (summaryEl) {
        summaryEl.innerHTML =
          '<h3 class="oms-fd-summary__heading">' + labels.summaryHeading + '</h3>' +
          '<ul class="oms-fd-summary__list">' + rowsHtml + '</ul>' +
          '<div class="oms-fd-summary__price-block">' +
          '<span class="oms-fd-summary__price-amount">' + labels.priceDisplay + '</span>' +
          '<span class="oms-fd-summary__price-saving">' + labels.savingDisplay + '</span></div>' +
          '<p class="oms-fd-summary__error" data-oms-fd-error' + (currentError ? '' : ' hidden') + '>' + escapeHtml(currentError) + '</p>' +
          '<button type="button" class="oms-fd-btn oms-fd-cta" aria-disabled="' + (!ready) + '">' + ctaLabel + '</button>';
      }

      if (stickyBarEl) {
        var status = (slots.tee1.variant ? '✓' : '○') + '&nbsp;Tee&nbsp;1&nbsp;&nbsp;' +
          (slots.tee2.variant ? '✓' : '○') + '&nbsp;Tee&nbsp;2&nbsp;&nbsp;' +
          (slots.hat.variant ? '✓' : '○') + '&nbsp;Hat';
        stickyBarEl.innerHTML =
          '<div class="oms-fd-sticky-bar__info">' +
          '<span class="oms-fd-sticky-bar__price">' + labels.priceDisplay + ' · ' + labels.savingDisplay + '</span>' +
          '<span class="oms-fd-sticky-bar__status">' + status + '</span></div>' +
          '<button type="button" class="oms-fd-btn oms-fd-cta" aria-disabled="' + (!ready) + '">' + ctaLabel + '</button>';
      }

      [summaryEl, stickyBarEl].forEach(function (container) {
        if (!container) return;
        container.querySelectorAll('.oms-fd-cta').forEach(function (btn) {
          btn.addEventListener('click', handleAddBundle);
        });
      });
    }

    function nudgeIncomplete() {
      var missing = !slots.tee1.variant ? 'tee1' : !slots.tee2.variant ? 'tee2' : !slots.hat.variant ? 'hat' : null;
      if (missing) {
        document.getElementById('oms-fd-' + missing + '-section-' + sectionId)
          ?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    // Pre-existing bug fixed in passing: this used to write directly into
    // the error <p> via the DOM, but the very next renderSummary() call
    // (always called right after, in handleAddBundle's finally) rebuilds
    // summaryEl.innerHTML from scratch with the error hardcoded `hidden`,
    // silently discarding whatever showError() had just set — so error
    // messages never actually displayed. Now it's just state that
    // renderSummary() reads when building the markup, so it survives.
    var currentError = '';
    function showError(message) {
      currentError = message || '';
    }

    // Reconciles the customer's CURRENT selections against the CURRENT
    // Shopify cart (fetched fresh, never assumed) every time this runs.
    // Never a blind re-add: only the lines that actually need to change
    // are touched, identified solely by this bundle's own _oms_fd_bundle /
    // _oms_fd_role properties — a customer's own independently-added
    // products are never inspected or altered. See planReconciliation()
    // and collectBundleLines() above for the diffing logic itself.
    async function handleAddBundle() {
      if (!isReady()) {
        nudgeIncomplete();
        return;
      }

      cartInProgress = true;
      showError('');
      renderSummary();

      try {
        var cart = await fetchCart();
        var plan = planReconciliation(cart, {
          tee1: slots.tee1.variant.id,
          tee2: slots.tee2.variant.id,
          hat: slots.hat.variant.id,
        });

        // Adds run BEFORE removes/quantity-fixes on purpose: if a new line
        // fails to add (sold out, a per-order quantity limit collides with
        // something already in cart, network error, etc.), the customer
        // must keep whatever correct bundle line they already had rather
        // than being left with that role missing entirely. A failed add
        // aborts here via the throw and nothing has been removed yet.
        if (plan.addItems.length > 0) {
          if (window.Unicorn && typeof window.Unicorn.addItemsToCart === 'function') {
            var result = await window.Unicorn.addItemsToCart({ items: plan.addItems });
            if (result && result.status && result.status >= 400) {
              throw new Error(typeof result.errors === 'string' ? result.errors : 'Could not update your bundle in cart.');
            }
          } else {
            // Unicorn unavailable — least-invasive Shopify-native fallback only.
            var res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: plan.addItems }),
            });
            if (!res.ok) {
              var errBody = await res.json().catch(function () { return {}; });
              throw new Error(errBody.message || errBody.description || 'Could not update your bundle in cart.');
            }
          }
        }

        for (var i = 0; i < plan.removeKeys.length; i++) {
          await changeCartLine(plan.removeKeys[i], 0);
        }
        for (var j = 0; j < plan.fixQuantityKeys.length; j++) {
          await changeCartLine(plan.fixQuantityKeys[j], 1);
        }

        // addItemsToCart marks its own request so Unicorn's fetch-observer skips it,
        // leaving the drawer showing pre-update state until explicitly refreshed.
        if (window.Unicorn && typeof window.Unicorn.refreshCart === 'function') {
          await window.Unicorn.refreshCart();
        }
        if (window.Unicorn && typeof window.Unicorn.openCart === 'function') {
          window.Unicorn.openCart();
        } else if (typeof window.unicornOpenCart === 'function') {
          window.unicornOpenCart();
        } else if (window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart) {
          window.Shopify.actions.openCart();
        } else {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }

      } catch (err) {
        showError('Something went wrong updating your bundle. Please try again.');
        if (window.console && console.error) console.error('[OMS Father\'s Day]', err);
      } finally {
        cartInProgress = false;
        renderSummary();
        // Re-check against a fresh /cart.js rather than assuming success set
        // this permanently true — a customer can remove every bundle line
        // via Unicorn's own cart UI at any time, entirely outside this
        // function, and the CTA label must not lie about that.
        refreshBundleLinePresence();
      }
    }

    renderSummary();
    refreshBundleLinePresence();
  }

  function initSizeGuideModal(root) {
    var modal = root.querySelector('[data-oms-fd-size-guide-modal]');
    var trigger = root.querySelector('[data-oms-fd-size-guide-trigger]');
    if (!modal || !trigger || modal.dataset.omsFdInit) return;
    modal.dataset.omsFdInit = 'true';

    var img = modal.querySelector('[data-oms-fd-lightbox-img]');
    var closeBtn = modal.querySelector('[data-oms-fd-lightbox-close]');
    var lastFocused = null;

    function onKeydown(e) {
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        closeBtn.focus();
      }
    }

    function open() {
      var srcImg = trigger.querySelector('[data-oms-fd-size-guide-src]');
      img.src = srcImg ? srcImg.getAttribute('data-oms-fd-size-guide-src') : srcImg.src;
      img.alt = srcImg ? srcImg.alt : 'Size guide';
      modal.hidden = false;
      document.body.classList.add('oms-fd-modal-open');
      lastFocused = trigger;
      closeBtn.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      modal.hidden = true;
      document.body.classList.remove('oms-fd-modal-open');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused) lastFocused.focus();
    }

    trigger.addEventListener('click', open);
    modal.addEventListener('click', close);
    modal.querySelector('.oms-fd-lightbox__dialog').addEventListener('click', function (e) {
      e.stopPropagation();
    });
    closeBtn.addEventListener('click', close);
  }

  function initAll(container) {
    (container || document).querySelectorAll('[data-oms-fd-builder]').forEach(function (root) {
      initBuilder(root);
      initSizeGuideModal(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
